import type { MouseEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate, type LinkProps, type NavLinkProps } from 'react-router-dom';
import { runCurtain } from '@/components/feature/routeCurtain';

function useCurtainClick(to: LinkProps['to'], onClick?: (e: MouseEvent<HTMLAnchorElement>) => void) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.currentTarget.getAttribute('target');
    if (target && target !== '_self') return;
    // Same-page anchors scroll normally; cross-page links (with or without #hash) get the curtain.
    if (typeof to !== 'string' || to.startsWith('#') || to.split('#')[0] === pathname) return;
    e.preventDefault();
    const handled = runCurtain(() => navigate(to));
    if (!handled) navigate(to);
  };
}

const curtainAttr = (to: LinkProps['to']) => (typeof to === 'string' && !to.startsWith('#') ? to : undefined);

export default function TransitionLink({ to, onClick, ...rest }: LinkProps) {
  const handle = useCurtainClick(to, onClick);
  return <Link to={to} onClick={handle} data-curtain-to={curtainAttr(to)} {...rest} />;
}

export function TransitionNavLink({ to, onClick, ...rest }: NavLinkProps) {
  const handle = useCurtainClick(to, onClick);
  return <NavLink to={to} onClick={handle} data-curtain-to={curtainAttr(to)} {...rest} />;
}